import { bindTouchGesture } from '@/util/touchGesture'

export interface SheetSwipeOptions {
    /** False while the surface is a centered card, where a downward drag does nothing. */
    readonly enabled: () => boolean
    readonly onDismiss: () => void
}

const START_PX = 8
const MIN_DISMISS_PX = 64
const DISMISS_FRACTION = 0.25

/**
 * Swipe-down-to-dismiss for a bottom sheet: a downward drag that starts while the sheet is scrolled
 * to its top pulls it along with the finger and closes it past a quarter of its height.
 */
export function bindSheetSwipe(sheet: HTMLElement, options: SheetSwipeOptions): () => void {
    let start: { x: number; y: number } | null = null
    let dragging = false
    let dy = 0

    const settle = (transition: string) => {
        sheet.style.transition = transition
        sheet.style.transform = ''
    }

    const finish = () => {
        if (dragging) {
            const dismiss = dy > Math.max(MIN_DISMISS_PX, sheet.offsetHeight * DISMISS_FRACTION)
            settle(dismiss ? 'none' : 'transform 200ms ease-out')
            if (dismiss) options.onDismiss()
        }
        start = null
        dragging = false
        dy = 0
    }

    return bindTouchGesture(sheet, {
        onTouchStart: (e) => {
            const touch = e.touches[0]
            start =
                e.touches.length === 1 && options.enabled() && !(sheet.scrollTop > 0)
                    ? { x: touch.clientX, y: touch.clientY }
                    : null
        },
        onTouchMove: (e) => {
            if (!start) return
            const touch = e.touches[0]
            const dx = touch.clientX - start.x
            dy = touch.clientY - start.y
            if (!dragging) {
                if (dy <= START_PX || Math.abs(dx) > dy) return
                dragging = true
                sheet.style.transition = 'none'
            }
            e.preventDefault()
            sheet.style.transform = `translateY(${Math.max(0, dy)}px)`
        },
        onTouchEnd: finish,
        onTouchCancel: () => {
            dy = 0
            finish()
        },
    })
}
